/**
 * @fileoverview Date and Group selection fields
 */
import type { FC } from 'react'
import { useArcTranslation } from 'hooks'
import { Stack, Group, Text, Select, Input } from 'components'
import { DateInput } from '@mantine/dates'
import { useAssignProjectContext } from './AssignProjectContext'
import { INPUT_STYLES, SPACING } from './styles'
import type { ProjectGroup } from './types'

interface DateGroupFieldsProps {
  groups: ProjectGroup[]
  isMobile: boolean
}

const RequiredLabel: FC<{ text: string }> = ({ text }) => (
  <Text component="span" size="sm" weight={500} color="dark">
    {text}
    <Text component="span" color="red" ml={2}>*</Text>
  </Text>
)

const ReadOnlyGroupField: FC<{ groupName: string; label: string }> = ({ groupName, label }) => (
  <Input.Wrapper label={<RequiredLabel text={label} />}>
    <Input
      value={groupName}
      readOnly
      styles={{
        ...INPUT_STYLES,
        input: { ...INPUT_STYLES.input, backgroundColor: '#fafafa', cursor: 'default' },
      }}
    />
  </Input.Wrapper>
)

export const DateGroupFields: FC<DateGroupFieldsProps> = ({ groups, isMobile }) => {
  const { t } = useArcTranslation()
  const { startDate, setStartDate, selectedGroupId, setSelectedGroupId } = useAssignProjectContext()

  const showGroupSelector = groups.length > 1
  const groupOptions = groups
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))

  const dateInput = (
    <DateInput
      label={<RequiredLabel text={t('assign_project.start_date')} />}
      value={startDate}
      onChange={setStartDate}
      placeholder=""
      styles={INPUT_STYLES}
      popoverProps={{ withinPortal: true }}
    />
  )

  const groupInput = showGroupSelector ? (
    <Select
      label={<RequiredLabel text={t('assign_project.project_group')} />}
      placeholder={t('assign_project.select_group')}
      value={selectedGroupId}
      onChange={setSelectedGroupId}
      data={groupOptions}
      styles={INPUT_STYLES}
      withinPortal
    />
  ) : groups.length === 1 ? (
    <ReadOnlyGroupField groupName={groups[0].name} label={t('assign_project.project_group')} />
  ) : null

  const Wrapper = isMobile ? Stack : Group
  const wrapperProps = isMobile
    ? { spacing: SPACING.element }
    : { grow: true, spacing: SPACING.element, align: 'flex-start' as const }

  return (
    <Wrapper {...wrapperProps}>
      {groupInput}
      {dateInput}
    </Wrapper>
  )
}
